import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';

const PortfolioManagement: React.FC = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(contentRef, { once: false, amount: 0.3 });

    // View states
    const [selectedAsset, setSelectedAsset] = useState<number | null>(null);
    const [showAllocation, setShowAllocation] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showAIInsights, setShowAIInsights] = useState<number | null>(null);

    type AssetSymbol = 'BTC' | 'ETH' | 'SOL';
    type Allocations = Record<AssetSymbol, number>;

    const [allocations, setAllocations] = useState<Allocations>({
        BTC: 55,
        ETH: 30,
        SOL: 15
    });

    // Parallax scroll effect
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"]
    });

    const backgroundY = useTransform(scrollYProgress, [0, 1], [0, 150]);

    // Portfolio demo data
    const portfolioItems = [
        { id: 1, name: 'Bitcoin', symbol: 'BTC' as AssetSymbol, value: 32450, change: 2.4, allocation: allocations.BTC, color: 'from-orange-500/70 to-orange-400/50' },
        { id: 2, name: 'Ethereum', symbol: 'ETH' as AssetSymbol, value: 1820, change: 3.8, allocation: allocations.ETH, color: 'from-blue-500/70 to-indigo-400/50' },
        { id: 3, name: 'Solana', symbol: 'SOL' as AssetSymbol, value: 76.5, change: 5.2, allocation: allocations.SOL, color: 'from-purple-500/70 to-fuchsia-400/50' },
    ];

    // Handle allocation changes
    const handleAllocationChange = (symbol: AssetSymbol, value: number) => {
        const currentTotal = Object.values(allocations).reduce((sum, val) => sum + val, 0);
        const oldValue = allocations[symbol];
        const newTotal = currentTotal - oldValue + value;

        if (newTotal <= 100) {
            setAllocations(prev => ({
                ...prev,
                [symbol]: value
            }));
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3,
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: [0.25, 0.25, 0, 1]
            }
        }
    };

    const viewTransition = {
        initial: { opacity: 0, x: 20 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -20 },
        transition: { duration: 0.3, ease: "easeInOut" }
    };

    // Handle AI analysis loading
    useEffect(() => {
        if (showAIInsights !== null) {
            setIsLoading(true);
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [showAIInsights]);

    return (
        <section
            ref={sectionRef}
            id="portfolio"
            className="section bg-primary relative overflow-hidden py-14"
        >
            {/* Background elements with parallax */}
            <motion.div
                className="absolute inset-0 pointer-events-none opacity-30"
                style={{ y: backgroundY }}
            >
                <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-accent/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-accent/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
            </motion.div>

            {/* Tech grid background */}
            <div className="absolute inset-0 opacity-5">
                <div className="h-full w-full grid grid-cols-12 gap-4">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="border-r border-white h-full" />
                    ))}
                </div>
                <div className="h-full w-full grid grid-rows-12 gap-4">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="border-b border-white w-full" />
                    ))}
                </div>
            </div>

            <div className="container-custom relative z-10" ref={contentRef}>
                <div className="flex flex-col md:flex-row items-start justify-between gap-8">
                    <motion.div
                        className="w-full md:w-5/12 md:sticky md:top-32"
                        initial={{ opacity: 0, x: -50 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="mb-4 inline-block px-3 py-1 bg-accent/10 rounded-full text-accent text-sm font-medium">
                            Portfolio Management
                        </div>
                        <h2 className="heading-lg mb-4">Smart, Intuitive <span className="text-accent">Portfolio</span> Management</h2>
                        <p className="text-gray-400 mb-6">
                            Track your crypto assets with precision and clarity. HaruKuro's portfolio management gives you a real-time view of your holdings with intelligent analytics and performance metrics.
                        </p>

                        <div className="space-y-4 mb-6">
                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Real-time Tracking</h3>
                                    <p className="text-gray-400 text-sm">Monitor your assets' performance in real-time with dynamic updates.</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Smart Allocation</h3>
                                    <p className="text-gray-400 text-sm">Visualize your portfolio allocation with interactive charts and insights.</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h5a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H3a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">AI Analytics</h3>
                                    <p className="text-gray-400 text-sm">Get personalized AI-powered insights and recommendations for your portfolio.</p>
                                </div>
                            </motion.div>
                        </div>

                        <motion.button
                            className="btn-primary mt-4"
                            whileHover={{
                                scale: 1.05,
                                boxShadow: '0 0 15px rgba(109, 40, 217, 0.5)'
                            }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Manage Portfolio
                        </motion.button>
                    </motion.div>

                    {/* Right content with view transitions */}
                    <div className="w-full md:w-7/12">
                        <div className="h-[600px] relative">
                            <AnimatePresence mode="wait">
                                {showAllocation ? (
                                    <motion.div
                                        key="allocation"
                                        className="absolute inset-0"
                                        {...viewTransition}
                                    >
                                        <div className="card bg-gradient-to-br from-secondary to-secondary/50 backdrop-blur-sm border border-white/5 p-6 h-full">
                                            <div className="flex justify-between items-center mb-6">
                                                <motion.button
                                                    className="text-accent hover:text-accent/80"
                                                    onClick={() => setShowAllocation(false)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                    </svg>
                                                </motion.button>
                                                <h3 className="text-xl font-medium">Portfolio Allocation</h3>
                                            </div>

                                            <div className="space-y-6">
                                                {portfolioItems.map((item) => (
                                                    <div key={item.symbol} className="space-y-2">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-sm font-medium">{item.name}</span>
                                                            <span className="text-sm text-accent">{allocations[item.symbol]}%</span>
                                                        </div>
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="100"
                                                            value={allocations[item.symbol]}
                                                            onChange={(e) => handleAllocationChange(item.symbol, parseInt(e.target.value))}
                                                            className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:cursor-pointer"
                                                            style={{
                                                                background: `linear-gradient(to right, #3B82F6 ${allocations[item.symbol]}%, rgba(255, 255, 255, 0.1) ${allocations[item.symbol]}%)`
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="mt-6 pt-6 border-t border-white/10">
                                                <div className="flex justify-between items-center text-sm">
                                                    <span>Total Allocation:</span>
                                                    <span className={`text-sm ${Object.values(allocations).reduce((a, b) => a + b, 0) === 100 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {Object.values(allocations).reduce((a, b) => a + b, 0)}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : selectedAsset ? (
                                    <motion.div
                                        key="asset-detail"
                                        className="absolute inset-0"
                                        {...viewTransition}
                                    >
                                        {portfolioItems.map((item) => (
                                            item.id === selectedAsset && (
                                                <div key={item.id} className="card bg-gradient-to-br from-secondary to-secondary/50 backdrop-blur-sm border border-white/5 p-6 h-full">
                                                    <div className="flex justify-between items-center mb-6">
                                                        <motion.button
                                                            className="text-accent hover:text-accent/80"
                                                            onClick={() => setSelectedAsset(null)}
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                        >
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                            </svg>
                                                        </motion.button>
                                                        <div className="flex items-center gap-4">
                                                            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                                                                <span className="font-mono font-bold text-white">{item.symbol}</span>
                                                            </div>
                                                            <div>
                                                                <h3 className="text-xl font-medium">{item.name}</h3>
                                                                <p className="text-sm text-gray-400">{item.allocation}% of portfolio</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">Current Value</p>
                                                            <p className="text-xl font-bold">${item.value.toLocaleString()}</p>
                                                        </div>
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">24h Change</p>
                                                            <p className={`text-xl font-bold ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                                {item.change >= 0 ? '+' : ''}{item.change}%
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">Market Cap</p>
                                                            <p className="text-lg font-medium">${(item.value * 1000000).toLocaleString()}</p>
                                                        </div>
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">Volume (24h)</p>
                                                            <p className="text-lg font-medium">${(item.value * 50000).toLocaleString()}</p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">Circulating Supply</p>
                                                            <p className="text-lg font-medium">
                                                                {item.symbol === 'BTC' ? '19.5M BTC' :
                                                                    item.symbol === 'ETH' ? '120.2M ETH' :
                                                                        '432.1M SOL'}
                                                            </p>
                                                        </div>
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">All-Time High</p>
                                                            <p className="text-lg font-medium">
                                                                {item.symbol === 'BTC' ? '$69,044' :
                                                                    item.symbol === 'ETH' ? '$4,891' :
                                                                        '$260.06'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">Market Dominance</p>
                                                            <p className="text-lg font-medium">
                                                                {item.symbol === 'BTC' ? '45.7%' :
                                                                    item.symbol === 'ETH' ? '18.2%' :
                                                                        '2.1%'}
                                                            </p>
                                                        </div>
                                                        <div className="bg-white/5 rounded-lg p-4">
                                                            <p className="text-sm text-gray-400 mb-1">Your Holdings</p>
                                                            <p className="text-lg font-medium">
                                                                {item.symbol === 'BTC' ? '0.85 BTC' :
                                                                    item.symbol === 'ETH' ? '12.4 ETH' :
                                                                        '156.2 SOL'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="relative">
                                                        <motion.button
                                                            className="absolute right-0 px-4 py-2 bg-accent/10 text-accent rounded-lg text-sm font-medium flex items-center gap-2"
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            onClick={() => setShowAIInsights(showAIInsights === item.id ? null : item.id)}
                                                            animate={{
                                                                y: [0, -5, 0],
                                                            }}
                                                            transition={{
                                                                duration: 2,
                                                                repeat: Infinity,
                                                                ease: "easeInOut"
                                                            }}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0110 2v5h5a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H3a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                                            </svg>
                                                            AI Insights
                                                        </motion.button>

                                                        <AnimatePresence>
                                                            {showAIInsights === item.id && (
                                                                <motion.div
                                                                    className="absolute right-0 bottom-12 w-[450px] bg-white rounded-lg p-4 shadow-xl border border-accent/10 z-10"
                                                                    initial={{ opacity: 0, y: 20 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    exit={{ opacity: 0, y: 20 }}
                                                                    transition={{ duration: 0.2 }}
                                                                >
                                                                    <div className="flex items-center mb-3">
                                                                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center mr-3`}>
                                                                            <span className="font-mono font-bold text-white text-xs">{item.symbol}</span>
                                                                        </div>
                                                                        <div>
                                                                            <h4 className="text-sm font-bold text-gray-800">{item.name} AI Analysis</h4>
                                                                            <p className="text-xs text-gray-500">Latest market insights & predictions</p>
                                                                        </div>
                                                                        <button
                                                                            className="ml-auto w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center"
                                                                            onClick={() => setShowAIInsights(null)}
                                                                        >
                                                                            <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                                                            </svg>
                                                                        </button>
                                                                    </div>

                                                                    <div className="w-full h-px bg-gray-100 my-3"></div>

                                                                    {isLoading ? (
                                                                        <div className="py-6 flex flex-col items-center">
                                                                            <div className="flex gap-2 mb-3">
                                                                                <motion.div
                                                                                    className="w-3 h-3 rounded-full bg-accent"
                                                                                    animate={{
                                                                                        scale: [1, 1.5, 1],
                                                                                        opacity: [0.5, 1, 0.5]
                                                                                    }}
                                                                                    transition={{
                                                                                        repeat: Infinity,
                                                                                        duration: 1.5,
                                                                                    }}
                                                                                />
                                                                                <motion.div
                                                                                    className="w-3 h-3 rounded-full bg-accent"
                                                                                    animate={{
                                                                                        scale: [1, 1.5, 1],
                                                                                        opacity: [0.5, 1, 0.5]
                                                                                    }}
                                                                                    transition={{
                                                                                        repeat: Infinity,
                                                                                        duration: 1.5,
                                                                                        delay: 0.2
                                                                                    }}
                                                                                />
                                                                                <motion.div
                                                                                    className="w-3 h-3 rounded-full bg-accent"
                                                                                    animate={{
                                                                                        scale: [1, 1.5, 1],
                                                                                        opacity: [0.5, 1, 0.5]
                                                                                    }}
                                                                                    transition={{
                                                                                        repeat: Infinity,
                                                                                        duration: 1.5,
                                                                                        delay: 0.4
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                            <p className="text-sm text-gray-500">AI analyzing {item.name} data...</p>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                                                                                {item.symbol === 'BTC' && "Bitcoin shows strong accumulation patterns from institutional investors. Recent ETF inflows and reduced selling pressure from miners suggest continued bullish momentum in the near term."}
                                                                                {item.symbol === 'ETH' && "Ethereum's ecosystem growth and decreased gas fees are positive signals. On-chain metrics show increased staking activity, which reduces available supply and suggests long-term holder confidence."}
                                                                                {item.symbol === 'SOL' && "Solana's transaction volume has increased 38% this month, with rising developer activity. Recent partnerships with financial institutions indicate growing institutional interest in the ecosystem."}
                                                                            </p>

                                                                            <div className="bg-accent/5 p-3 rounded-lg">
                                                                                <h5 className="text-xs font-semibold text-accent mb-1">AI Recommendation</h5>
                                                                                <p className="text-xs text-gray-700">
                                                                                    {item.symbol === 'BTC' && "Consider maintaining or slightly increasing your Bitcoin allocation as the upcoming halving historically precedes bull markets."}
                                                                                    {item.symbol === 'ETH' && "Hold your current Ethereum position. The upcoming protocol upgrades could significantly impact price action in Q3."}
                                                                                    {item.symbol === 'SOL' && "Monitor closely. While fundamentals are improving, Solana still experiences higher volatility compared to BTC and ETH."}
                                                                                </p>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </div>
                                                </div>
                                            )
                                        ))}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="portfolio-overview"
                                        className="absolute inset-0"
                                        {...viewTransition}
                                    >
                                        {/* Portfolio Overview Card - Always visible */}
                                        <motion.div
                                            className="card bg-gradient-to-br from-secondary to-secondary/50 backdrop-blur-sm border border-white/5 overflow-hidden mb-3"
                                            variants={itemVariants}
                                        >
                                            <div className="p-4">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-xl font-medium">Portfolio Overview</h3>
                                                    <motion.div
                                                        className="text-xs bg-accent/20 text-accent px-2 py-1 rounded-full"
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                    >
                                                        Last 24h: +2.8%
                                                    </motion.div>
                                                </div>

                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-2xl font-bold">$48,765.32</span>
                                                    <motion.span
                                                        className="text-green-400 text-sm flex items-center"
                                                        animate={{
                                                            opacity: [0.8, 1, 0.8],
                                                        }}
                                                        transition={{ duration: 2, repeat: Infinity }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                        </svg>
                                                        2.8%
                                                    </motion.span>
                                                </div>
                                                <p className="text-gray-400 text-sm mb-4">Total Portfolio Value</p>

                                                {/* Portfolio Allocation Chart */}
                                                <div className="relative h-8 w-full bg-white/5 rounded-lg overflow-hidden mb-3">
                                                    {portfolioItems.map((item, index) => {
                                                        const previousAllocations = portfolioItems
                                                            .slice(0, index)
                                                            .reduce((sum, i) => sum + i.allocation, 0);

                                                        return (
                                                            <motion.div
                                                                key={item.id}
                                                                className={`absolute top-0 h-full bg-gradient-to-r ${item.color}`}
                                                                style={{
                                                                    left: `${previousAllocations}%`,
                                                                    width: `${item.allocation}%`
                                                                }}
                                                                whileHover={{
                                                                    y: -2,
                                                                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>

                                                <div className="flex flex-wrap gap-3 mb-2">
                                                    {portfolioItems.map((item) => (
                                                        <motion.div
                                                            key={item.id}
                                                            className="flex items-center gap-1 text-xs"
                                                            whileHover={{ scale: 1.1 }}
                                                        >
                                                            <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${item.color}`} />
                                                            <span>{item.symbol} {item.allocation}%</span>
                                                        </motion.div>
                                                    ))}
                                                </div>
                                            </div>
                                        </motion.div>

                                        {/* Assets List */}
                                        <div className="space-y-3">
                                            {portfolioItems.map((item) => (
                                                <motion.div
                                                    key={item.id}
                                                    className="card flex items-center justify-between hover:bg-secondary/80 cursor-pointer"
                                                    variants={itemVariants}
                                                    whileHover={{
                                                        x: 5,
                                                        transition: { duration: 0.2 }
                                                    }}
                                                    whileTap={{ scale: 0.98 }}
                                                    onClick={() => setSelectedAsset(item.id)}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                                                            <span className="font-mono font-bold text-white">{item.symbol}</span>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-medium">{item.name}</h4>
                                                            <p className="text-sm text-gray-400">{item.allocation}% of portfolio</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-mono font-medium">${item.value.toLocaleString()}</div>
                                                        <div className={`text-sm ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                            {item.change >= 0 ? '+' : ''}{item.change}%
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* Allocation Controls - Only this part fades */}
                                        <AnimatePresence>
                                            {showAllocation && (
                                                <motion.div
                                                    className="absolute inset-0 bg-secondary/95 backdrop-blur-sm"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <div className="p-6">
                                                        <div className="flex justify-between items-center mb-6">
                                                            <motion.button
                                                                className="text-accent hover:text-accent/80"
                                                                onClick={() => setShowAllocation(false)}
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                            >
                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                                </svg>
                                                            </motion.button>
                                                            <h3 className="text-xl font-medium">Adjust Allocation</h3>
                                                        </div>

                                                        <div className="space-y-6">
                                                            {portfolioItems.map((item) => (
                                                                <div key={item.symbol} className="space-y-2">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-sm font-medium">{item.name}</span>
                                                                        <span className="text-sm text-accent">{allocations[item.symbol]}%</span>
                                                                    </div>
                                                                    <input
                                                                        type="range"
                                                                        min="0"
                                                                        max="100"
                                                                        value={allocations[item.symbol]}
                                                                        onChange={(e) => handleAllocationChange(item.symbol, parseInt(e.target.value))}
                                                                        className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:cursor-pointer"
                                                                        style={{
                                                                            background: `linear-gradient(to right, #3B82F6 ${allocations[item.symbol]}%, rgba(255, 255, 255, 0.1) ${allocations[item.symbol]}%)`
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <div className="mt-6 pt-6 border-t border-white/10">
                                                            <div className="flex justify-between items-center text-sm">
                                                                <span>Total Allocation:</span>
                                                                <span className={`text-sm ${Object.values(allocations).reduce((a, b) => a + b, 0) === 100 ? 'text-green-400' : 'text-red-400'}`}>
                                                                    {Object.values(allocations).reduce((a, b) => a + b, 0)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PortfolioManagement; 